import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Typography
} from '@mui/joy';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import BiotechOutlinedIcon from '@mui/icons-material/BiotechOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import PrecisionManufacturingOutlinedIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';
import { useAuth } from '../context/AuthContext';
import { PageHeaderActionsProvider, usePageHeaderActions } from '../context/PageHeaderActionsContext';
import LocusLogo from './LocusLogo';
import Footer from './Footer';
import './Navigation.css';

const SIDEBAR_WIDTH = 310;

const PAGE_TITLES = {
  '/stock': 'Materials',
  '/disposables': 'Disposable Materials',
  '/implants': 'Implants',
  '/instruments': 'Instruments',
  '/equipment': 'Equipment',
  '/add-item': 'Add Stock',
  '/vendors': 'Vendors',
  '/reports': 'Reports',
  '/reports/instruments': 'Instruments Report',
  '/reports/equipment': 'Equipment Report'
};

export const navItems = [
  { label: 'Materials', to: '/stock', icon: Inventory2OutlinedIcon },
  { label: 'Disposables', to: '/disposables', icon: MedicalServicesOutlinedIcon },
  { label: 'Implants', to: '/implants', icon: BiotechOutlinedIcon },
  { label: 'Instruments', to: '/instruments', icon: PrecisionManufacturingOutlinedIcon },
  { label: 'Equipment', to: '/equipment', icon: BuildOutlinedIcon },
  { label: 'Add Stock', to: '/add-item', icon: AddBoxOutlinedIcon },
  { label: 'Vendors', to: '/vendors', icon: StoreOutlinedIcon },
  { label: 'Reports', to: '/reports', icon: AssessmentOutlinedIcon },
  { label: 'Instrument Reports', to: '/reports/instruments', icon: BarChartOutlinedIcon },
  { label: 'Equipment Reports', to: '/reports/equipment', icon: InsertChartOutlinedIcon }
];

const sidebarLinkSx = (active) => ({
  justifyContent: 'flex-start',
  width: '100%',
  color: 'white',
  fontWeight: active ? 600 : 400,
  fontSize: '0.875rem',
  py: 1,
  px: 1.5,
  borderRadius: 'sm',
  backgroundColor: active ? 'rgba(255,255,255,0.18)' : 'transparent',
  '&:hover': {
    backgroundColor: active ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.1)'
  }
});

const logoutButtonSx = {
  width: '100%',
  color: 'white',
  borderColor: 'rgba(255,255,255,0.6)',
  justifyContent: 'flex-start',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderColor: 'white'
  }
};

const SidebarPanel = ({ onLogout, onNavigate }) => {
  const location = useLocation();

  return (
    <>
      <Box
        className="sidebar-logo-header"
        sx={{
          px: 2,
          pt: 1.5,
          pb: 1,
          overflow: 'visible',
          flexShrink: 0
        }}
      >
        <LocusLogo
          variant="nav"
          color="white"
          width={270}
          sx={{ mx: 'auto' }}
        />
      </Box>

      <Box
        className="sidebar-nav-menu"
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 1.5,
          pt: 1,
          pb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          borderTop: '1.5px solid rgba(255, 255, 255, 0.55)'
        }}
      >
        {navItems.map((it) => {
          const Icon = it.icon;
          const active = location.pathname === it.to;
          return (
            <Button
              key={it.to}
              component={Link}
              to={it.to}
              variant="plain"
              size="sm"
              startDecorator={<Icon fontSize="small" />}
              onClick={onNavigate}
              sx={sidebarLinkSx(active)}
            >
              {it.label}
            </Button>
          );
        })}
      </Box>

      <Box sx={{ px: 1.5, py: 1.5, flexShrink: 0 }}>
        <Divider sx={{ mb: 1.5, borderColor: 'rgba(255,255,255,0.2)' }} />
        <Button
          onClick={() => {
            onNavigate?.();
            onLogout();
          }}
          variant="outlined"
          size="sm"
          startDecorator={<LogoutOutlinedIcon fontSize="small" />}
          sx={logoutButtonSx}
        >
          Logout
        </Button>
      </Box>
    </>
  );
};

const DesktopSidebar = ({ onLogout }) => (
  <Box
    component="nav"
    className="app-sidebar"
    sx={{
      width: SIDEBAR_WIDTH,
      flexShrink: 0,
      backgroundColor: 'primary.500',
      display: { xs: 'none', md: 'flex' },
      flexDirection: 'column',
      minHeight: '100vh',
      position: 'sticky',
      top: 0,
      alignSelf: 'flex-start'
    }}
  >
    <SidebarPanel onLogout={onLogout} />
  </Box>
);

const MobileDrawer = ({ open, onClose, onLogout }) => (
  <Drawer
    open={open}
    onClose={onClose}
    anchor="left"
    size="sm"
    slotProps={{
      root: { sx: { display: { md: 'none' } } },
      content: {
        sx: {
          p: 0,
          width: SIDEBAR_WIDTH,
          maxWidth: '85vw',
          backgroundColor: 'primary.500'
        }
      }
    }}
  >
    <Box
      className="app-sidebar"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'primary.500'
      }}
    >
      <SidebarPanel onLogout={onLogout} onNavigate={onClose} />
    </Box>
  </Drawer>
);

const TopBar = ({ title, activeItem, onMenuOpen }) => {
  const ActiveIcon = activeItem?.icon;
  const { headerAction } = usePageHeaderActions();

  return (
    <Box
      component="header"
      sx={{
        backgroundColor: 'background.surface',
        px: { xs: 1.5, sm: 3 },
        py: { xs: 1.25, sm: 2 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minHeight: { xs: 56, sm: 64 }
      }}
    >
      <IconButton
        variant="plain"
        className="topbar-menu-btn"
        aria-label="Open menu"
        onClick={onMenuOpen}
        sx={{ display: { xs: 'inline-flex', md: 'none' }, flexShrink: 0 }}
      >
        <MenuIcon />
      </IconButton>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
        {ActiveIcon && (
          <Box className="topbar-page-icon" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
            <ActiveIcon fontSize="medium" />
          </Box>
        )}
        <Typography
          level="h2"
          sx={{
            color: 'primary.500',
            fontWeight: 600,
            fontSize: { xs: '1.125rem', sm: '1.5rem' },
            lineHeight: 1.2
          }}
        >
          {title}
        </Typography>
      </Box>

      {headerAction && (
        <Box sx={{ flexShrink: 0, ml: 'auto' }}>
          {headerAction}
        </Box>
      )}
    </Box>
  );
};

export const getPageTitle = (pathname) => PAGE_TITLES[pathname] || 'Locus Stock';

export const getActiveNavItem = (pathname) =>
  navItems.find((it) => it.to === pathname) || null;

const STOCK_LIST_PATHS = new Set(['/stock', '/disposables', '/implants', '/instruments', '/equipment']);

const AppLayout = ({ children }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const title = getPageTitle(location.pathname);
  const activeItem = getActiveNavItem(location.pathname);
  const isStockListPage = STOCK_LIST_PATHS.has(location.pathname);
  const isVendorsPage = location.pathname === '/vendors';

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', height: '100%' }}>
      <DesktopSidebar onLogout={handleLogout} />
      <MobileDrawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        onLogout={handleLogout}
      />
      <PageHeaderActionsProvider>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: '100vh',
            height: { xs: '100dvh', md: 'auto' },
            maxHeight: { xs: '100dvh', md: 'none' },
            overflow: { xs: 'hidden', md: 'visible' }
          }}
        >
          <TopBar
            title={title}
            activeItem={activeItem}
            onMenuOpen={() => setMobileOpen(true)}
          />
          <Box
            component="main"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: isVendorsPage
                ? 'hidden'
                : isStockListPage
                  ? { xs: 'hidden', md: 'auto' }
                  : 'auto',
              WebkitOverflowScrolling: 'touch',
              pb: { xs: 0, md: '5px' }
            }}
          >
            {children}
          </Box>
          <Footer />
        </Box>
      </PageHeaderActionsProvider>
    </Box>
  );
};

export default AppLayout;
